import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Toggles } from '../features-data/sub-models/toggles.model';
import { fromJS, List, Map } from 'immutable';
import { PromoBanner } from './models';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { SETTINGS_MASTER_ROUTE } from '../main/main.data';
import { TrackingObjectBuilder } from '../tracking/tracking-object.builder';
import { IconSizesEnum } from '../shared/components/icon/icons-sizes.enum';
import { XERO_PROMO_LOGO_SRC } from '../xero-data/xero-data.data';
import {
  SETTINGS_INVOICE_DESIGN_ROUTE,
  SETTINGS_XERO_ROUTE,
} from '../settings/settings.data';
import { XERO_INTEGRATION_ROUTE } from '../xero/xero.constant';
import { setLocalStorageItem } from '../browser-manager-data/browser-manager-data.actions';
import {
  popCurrentPromoBanner,
  triggerPostPurchaseSurvey,
} from './home-data.actions';
import { SettingsDataBuilder } from '../settings-data/settings-data.builder';
import { Settings } from '../settings-data/settings.model';
import { TrackingScreenNamesEnum } from '../tracking/tracking.data';
import { Features } from '../features-data/sub-models/features.model';
import {
  PostPurchaseInfo,
  PostPurchaseStateEnum,
} from '../settings-data/sub-models/post-purchase-info.model';
import { SettingsContent } from '../settings-data/sub-models/settings-content.model';

@Injectable()
export class HomeDataBuilder {
  constructor(
    private _localStorageService: LocalStorageService,
    private _translateService: TranslateService,
    private _settingsDataBuilder: SettingsDataBuilder,
    private _trackingObjectBuilder: TrackingObjectBuilder
  ) {}

  buildPromoBannerStack(
    accountId: string,
    feature: Features,
    settings: Settings
  ): List<PromoBanner> {
    const promoBanners: PromoBanner[] = []; // higher priority first
    const { toggles } = feature;

    // post purchase survey
    const isPostPurchaseDone = settings.postPurchaseInfo.isSurveyDone();
    const showPostPurchase =
      toggles.isAllAccess(Toggles.KEYS.POST_PURCHASE_SURVEY) &&
      !isPostPurchaseDone;

    if (showPostPurchase) {
      promoBanners.push(this.buildPostPurchaseBanner());
    }

    // customize invoice
    const customizeInvoiceKey = this.buildCustomizeInvoicePromoKey(accountId);
    const showCustomizeInvoice = !this._localStorageService.getItem(
      customizeInvoiceKey
    );

    // hide customize invoice if we've the same in home tasks
    const showingHomeTasks =
      toggles.isAllAccess(Toggles.KEYS.FTU_EXPERIMENT) &&
      toggles.isAllAccess(Toggles.KEYS.FTU_TASK_LIST);

    if (showCustomizeInvoice && !showingHomeTasks) {
      promoBanners.push(this.buildCustomizeInvoiceBanner(customizeInvoiceKey));
    }

    // xero
    const xeroPromoKey = this.buildXeroPromoKey(accountId);
    const showXero =
      toggles.isAllAccess(Toggles.KEYS.XERO) &&
      !this._localStorageService.getItem(xeroPromoKey);

    if (showXero) {
      promoBanners.push(this.buildXeroPromoBanner(xeroPromoKey));
    }

    return List(promoBanners);
  }

  buildPostPurchaseBanner(): PromoBanner {
    return new PromoBanner({
      description: this._translateService.instant(
        'HOME.BANNER.POST_PURCHASE.BODY'
      ),
      icon: {
        name: 'post-purchase-banner',
        size: IconSizesEnum.custom,
      },
      cta1: {
        label: this._translateService.instant('HOME.BANNER.POST_PURCHASE.CTA'),
        route: [],
      },
      cta2: {
        label: this._translateService.instant(
          'HOME.BANNER.POST_PURCHASE.CANCEL'
        ),
        route: [],
      },
      cta1_actions: [
        triggerPostPurchaseSurvey(
          this._translateService.instant(
            'DIALOG.POST_PURCHASE.NON_PURCHASE.TITLE'
          ),
          this._translateService.instant(
            'DIALOG.POST_PURCHASE.NON_PURCHASE.HEADER'
          ),
          TrackingScreenNamesEnum.personalization_postpurchase_nonpurchase
        ),
        popCurrentPromoBanner(),
      ],
      cta2_actions: [popCurrentPromoBanner()],
      hide_close: true,
      tracking_object: this._trackingObjectBuilder.buildPromoBanner(
        'post_purchase'
      ),
    });
  }

  buildXeroPromoBanner(localStorageKey: string): PromoBanner {
    return new PromoBanner({
      title: this._translateService.instant('XERO.IN_APP_PROMO.TITLE'),
      description: this._translateService.instant('XERO.IN_APP_PROMO.BODY'),
      logo_src: XERO_PROMO_LOGO_SRC,
      cta1: {
        label: this._translateService.instant('XERO.IN_APP_PROMO.CTA'),
        route: [
          SETTINGS_MASTER_ROUTE,
          SETTINGS_XERO_ROUTE,
          XERO_INTEGRATION_ROUTE,
        ],
      },
      cta1_actions: [setLocalStorageItem(localStorageKey, true)], // mark banner as dismissed
      local_storage_key: localStorageKey,
      tracking_object: this._trackingObjectBuilder.buildPromoBanner(
        'xero_promo'
      ),
    });
  }

  buildCustomizeInvoiceBanner(localStorageKey: string): PromoBanner {
    return new PromoBanner({
      title: this._translateService.instant(
        `HOME.BANNER.CUSTOMIZE_INVOICE.TITLE`
      ),
      description: this._translateService.instant(
        `HOME.BANNER.CUSTOMIZE_INVOICE.BODY`
      ),
      icon: {
        name: 'customize-invoice',
        size: IconSizesEnum.custom,
      },
      cta1: {
        label: this._translateService.instant(
          `HOME.BANNER.CUSTOMIZE_INVOICE.CTA`
        ),
        route: [SETTINGS_MASTER_ROUTE, SETTINGS_INVOICE_DESIGN_ROUTE],
      },
      cta1_actions: [setLocalStorageItem(localStorageKey, true)], // mark banner as dismissed
      local_storage_key: localStorageKey,
      tracking_object: this._trackingObjectBuilder.buildPromoBanner(
        'invoice_customization'
      ),
    });
  }

  buildXeroPromoKey(accountId: string) {
    return `xero_promo_${accountId}`;
  }

  buildCustomizeInvoicePromoKey(accountId: string) {
    return `customize_invoice_promo_${accountId}`;
  }

  buildPostPurchaseSurveyKey(accountId: string) {
    return `post_purchase_survey_${accountId}`;
  }

  buildSettingsPostPurchaseInfoProps(
    state: PostPurchaseStateEnum
  ): Map<string, any> {
    return fromJS({
      [SettingsContent.KEYS.POST_PURCHASE_INFO]: {
        [PostPurchaseInfo.KEYS.STATE]: state,
      },
    });
  }
}
